import {
  Autocomplete,
  Chip,
  InputAdornment,
  Paper,
  TextField,
  Checkbox,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import {
  LineChartPro,
  lineElementClasses,
  LinePlot,
  markElementClasses,
  MarkPlot,
  CurveType,
} from "@mui/x-charts-pro";
import { ChartsGrid } from "@mui/x-charts-pro";
import { CustomItemTooltip } from "../../../../../ResuableChartTooltip/CustomItemTooltip";
import {useEffect, useMemo, useState } from "react";
import { ChartSkeleton } from "../../../../../ChartSkeleton";
import { useSearchParams } from "react-router-dom";

interface QueryItem {
  matchtype: string;
  avg_top_impshare: number;
  display_url_domain: string;
  kw_name: string;
  activity_date: string;
  account_source: string;
  kw_id: number;
}

interface ChartDataItem {
  date: string;
  [key: string]: number | string;
}

interface KeywordOption {
  label: string;
}

interface Props {
  dailyAverage?: {
    query_output: QueryItem[];
  };
}

const getColorFromIndex = (index: number): string => {
  // Base colors for each group
  const baseColors = {
    blue: { h: 210, s: 100 },
    red: { h: 0, s: 100 },
    green: { h: 120, s: 100 },
  };

  // Get color group based on index
  const group = Object.values(baseColors)[index % 3];

  // Generate dynamic brightness value (70-100%)
  const l = 70 + ((index * 7) % 30);

  // Return HSL color
  return `hsl(${group.h}, ${group.s}%, ${l}%)`;
};

const DailyaverageImpressionShare = ({ dailyAverage }: Props) => {
  const [selectedKeyword, setSelectedKeyword] = useState<KeywordOption | null>({
    label: "All",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [showMarks] = useState(false);
  const insightType = searchParams.get("insightType");
  const [selectedCompetitors, setSelectedCompetitors] = useState([]) as any;

  // Extract unique keywords
  const keywords = useMemo(() => {
    const uniqueKeywords = new Set(
      dailyAverage?.query_output?.map((item) => item.kw_name)
    );
    return [
      { label: "All" },
      ...Array.from(uniqueKeywords).map((kw) => ({ label: String(kw) })),
    ];
  }, [dailyAverage?.query_output]);

  // Extract Competitor names
  const competitors = useMemo(() => {
      const uniqueDomains = new Set(
          dailyAverage?.query_output?.map((item) => item.display_url_domain)
      );
      // Filter out "You" from the competitors list
      return Array.from(uniqueDomains).filter((domain) => domain !== "You");
  }, [dailyAverage?.query_output]);

  useEffect(() => {
    if(competitors.length > 0) {
      // Only preselect defaults when there is no current selection (avoid overwriting user choices)
      if (!selectedCompetitors || selectedCompetitors.length === 0) {
        setSelectedCompetitors(["You", ...competitors.slice(0,5)]);
      }
    }
  }, [competitors, selectedCompetitors]);

  const formattedData = dailyAverage?.query_output?.map((item) => {
    const [year, month, day] = item.activity_date.split("-");
    return {
      ...item,
      activity_date: `${day}/${month}/${year}`,
    };
  });

  const sortedData = formattedData
    ?.filter((d: any) => d?.activity_date) // ensure activity_date exists
    .sort((a: any, b: any) => {
      const dateA = new Date(a.activity_date.split("/").reverse().join("/"));
      const dateB = new Date(b.activity_date.split("/").reverse().join("/"));
      return dateA.getTime() - dateB.getTime();
    });

  // Generate chart data - filter by selected competitors
  const chartData = useMemo((): ChartDataItem[] => {
    if (!sortedData) return [];

    let filteredData = sortedData;
    if (selectedKeyword && selectedKeyword.label !== "All") {
      filteredData = filteredData.filter(
        (item) => item.kw_name === selectedKeyword.label
      );
    }

    // Filter by selected competitors (always include "You")
    if (selectedCompetitors.length > 0) {
        filteredData = filteredData.filter(
            (item) => item.display_url_domain === "You" || selectedCompetitors.includes(item.display_url_domain)
        );
    } else {
        // If no competitors selected, show only "You"
        filteredData = filteredData.filter((item) => item.display_url_domain === "You");
    }

    // Get all unique dates
    const allDates = [
      ...new Set(filteredData.map((item) => item.activity_date)),
    ].sort((a, b) => {
      const dateA = new Date(a.split("/").reverse().join("/"));
      const dateB = new Date(b.split("/").reverse().join("/"));
      return dateA.getTime() - dateB.getTime();
    });

    // Group by date and domain
    const groupedData = filteredData.reduce((acc, curr) => {
      const key = `${curr.activity_date}-${curr.display_url_domain}`;

      if (!acc[key]) {
        acc[key] = {
          values: [] as number[],
          date: curr.activity_date,
          domain: curr.display_url_domain,
        };
      }
      acc[key].values.push(curr.avg_top_impshare);
      return acc;
    }, {} as Record<string, { values: number[]; date: string; domain: string }>);

    // Calculate averages and format data
    const chartDataMap: Record<string, ChartDataItem> = {};

    // Initialize all dates
    allDates.forEach((date) => {
      const formattedDate = new Date(
        date.split("/").reverse().join("/")
      ).toLocaleDateString("en-GB");
      chartDataMap[formattedDate] = { date: formattedDate };
    });
    // Fill in the data for each domain and date combination
    Object.values(groupedData).forEach(({ values, date, domain }) => {
      const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
      const formattedDate = new Date(
        date.split("/").reverse().join("/")
      ).toLocaleDateString("en-GB");
      const safeKey = domain.replace(/[.\s]/g, "_");

      if (chartDataMap[formattedDate]) {
        chartDataMap[formattedDate][safeKey] = avg;
      }
    });

    return Object.values(chartDataMap);
  }, [sortedData, selectedKeyword, selectedCompetitors]);

  // Generate series configuration
  const seriesConfig = useMemo(() => {
    if (!chartData.length) return [];

    const allDomainKeys = new Set<string>();
    chartData.forEach((dataItem) => {
      Object.keys(dataItem).forEach((key) => {
        if (key !== "date") {
          allDomainKeys.add(key);
        }
      });
    });

    const domains = Array.from(allDomainKeys).map((domain) => {
      const isYou = domain === "You";
      // Use original competitors array index for consistent colors
      const originalDomain = domain.replace(/_/g, ".");
      const originalIndex = competitors.indexOf(originalDomain);
      const colorIndex = originalIndex !== -1 ? originalIndex : 0;

      return {
        id: isYou ? "optival-line" : `competitor-${originalIndex}`,
        dataKey: domain,
        color: isYou ? "#6200EE" : getColorFromIndex(colorIndex),
        curve: "linear" as CurveType,
        strokeWidth: isYou ? 3 : 2,
        zIndex: isYou ? 1 : 0,
        label: isYou ? "Optival" : originalDomain,
      };
    });

    return domains;
  }, [chartData, competitors]);

  // Build a map of dataKey -> color so tooltips can use the exact same colors as chart series
  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    (seriesConfig || []).forEach((s: any) => {
      if (s && s.dataKey && s.color) {
        map[s.dataKey] = s.color as string;
      }
    });
    return map;
  }, [seriesConfig]);

  // Calculate min/max values from chart data
  const yAxisRange = useMemo(() => {
    if (!chartData.length) {
      return { min: 0, max: 100 };
    }

    const allValues: number[] = [];

    // Collect all impression share values from all domains
    chartData.forEach((dataPoint) => {
      Object.keys(dataPoint).forEach((key) => {
        if (key !== "date") {
          const value = dataPoint[key];
          if (typeof value === "number" && !isNaN(value)) {
            allValues.push(value);
          }
        }
      });
    });

    if (allValues.length === 0) {
      return { min: 0, max: 100 };
    }

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    // Add some padding (5% of range) to make the chart more readable
    const range = max - min;
    const padding = range * 0.05;

    return {
      min: Math.max(0, Math.floor(min - padding)), // Don't go below 0
      max: Math.min(100, Math.ceil(max + padding)), // Don't go above 100 for percentage values
    };
  }, [chartData]);

  const handleKeywordChange = (_: any, newValue: KeywordOption | null) => {
    setIsLoading(true);
    setSelectedKeyword(newValue);

    // If the keyword is cleared, ensure competitors show only "You"
    if (newValue === null) {
      setSelectedCompetitors(["You"]);
    }

    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  if (isLoading) {
    return <ChartSkeleton />;
  }

  return (
    <Paper sx={{ marginTop: "8px" }}>
      <Stack
        flexDirection={"row"}
        justifyContent="space-between"
        alignItems={"center"}
        padding={2}
      >
        <Stack
          direction="column"
          justifyContent="space-between"
          alignItems="left"
        >
          <Typography fontSize={18}>
            Daily average of "Top Impression Share"
          </Typography>
        </Stack>
        <Autocomplete
          size="small"
          disablePortal
          disableClearable
          value={selectedKeyword ?? undefined}
          data-tracker={insightType === "adgroup" ? "310" : "331"}
          onChange={handleKeywordChange}
          options={keywords.filter(
            (option) => option.label !== selectedKeyword?.label
          )}
          sx={{ width: 272 }}
          renderInput={(params) => (
            <TextField
              color="secondary"
              {...params}
              label="Keyword"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ marginLeft: "12px" }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Stack>
      <Box sx={{ width: "100%", height: 450 }}>
        <Autocomplete
          multiple
          freeSolo
          size="small"
          disableCloseOnSelect
          options={(() => {
            const nonYou = (competitors || []).filter((c: string) => c !== "You");
            const isAllSelected = selectedCompetitors.length === nonYou.length + 1; // +1 for 'You'
            return [isAllSelected ? "Deselect All" : "Select All", "You", ...nonYou];
          })()}
          getOptionLabel={(option) => (option === "You" ? "Optival" : String(option))}
          onChange={(_event, newValue) => {
            const nonYou = (competitors || []).filter((c: string) => c !== "You");
            if (newValue.includes("Select All")) {
              setSelectedCompetitors(["You", ...nonYou]);
            } else if (newValue.includes("Deselect All")) {
              setSelectedCompetitors(["You"]);
            } else {
              const cleaned = newValue.filter((v: string) => v !== "Select All" && v !== "Deselect All");
              const ensured = Array.from(new Set(["You", ...cleaned]));
              setSelectedCompetitors(ensured as any);
            }
          }}
          value={selectedCompetitors}
          sx={{ width: "100%", padding: "8px 16px" }}
          renderOption={(props, option, { selected }) => {
            const nonYou = (competitors || []).filter((comp: string) => comp !== "You");
            const isSelectAll = option === "Select All";
            const isDeselectAll = option === "Deselect All";
            if (option === "You") {
              return (
                <li {...props} style={{ fontWeight: 600, borderBottom: "1px solid #eee" }}>
                  <Checkbox size="small" checked color="secondary" disabled sx={{ mr: 1 }} />
                  <span style={{ color: "#6b46c1", fontWeight: 600 }}>Optival</span>
                </li>
              );
            }

            const isChecked = isSelectAll || isDeselectAll ? selectedCompetitors.length === nonYou.length + 1 : selected;
            return (
              <li
                {...props}
                style={
                  isSelectAll || isDeselectAll
                    ? {
                      fontWeight: 600,
                      backgroundColor: "#f7f7f7",
                      borderBottom: "1px solid #ddd",
                    }
                    : {}
                }
              >
                <Checkbox size="small" checked={isChecked} color="secondary" sx={{ mr: 1 }} />
                {String(option)}
              </li>
            );
          }}
          renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                  if (option === "You") {
                      return (
                          <Chip
                              key={option}
                              label={"Optival"}
                              color="secondary"
                              size="small"
                              variant="filled"
                              sx={{
                                  backgroundColor: "#fff",
                                  color: "secondary.main",
                                  border: "1px solid rgba(0,0,0,0.12)",
                                  fontWeight: 600,
                              }}
                          />
                      );
                  }

                  return (
                      <Chip
                          label={option}
                          {...getTagProps({ index })}
                          color="secondary"
                          size="small"
                          variant="filled"
                      />
                  );
              })
          }
          renderInput={(params) => (
              <TextField
                  color="secondary"
                  {...params}
                  label="Competitors"
                  slotProps={{
                      inputLabel: {
                          shrink: true,
                      },
                  }}
                  InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                          <>
                              <InputAdornment position="start">
                                  <SearchIcon sx={{ marginLeft: "12px" }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                          </>
                      ),
                  }}
              />
          )}
        />
        <LineChartPro
          dataset={chartData}
          xAxis={[{ scaleType: "band", dataKey: "date" }]}
          yAxis={[
            {
              id: "percentage-axis",
              valueFormatter: (value: number) => `${value}%`,
              min: yAxisRange.min,
              max: yAxisRange.max,
            },
          ]}
          series={seriesConfig}
          margin={{ left: 48, right: 56, top: 25, bottom: 84 }}
          leftAxis={null}
          rightAxis={{}}
          sx={{
            [`& .${lineElementClasses.root}.MuiLineElement-series-optival-line`]: {
              strokeWidth: 3,
              opacity: 1,
            },
            // All other lines
            [`& .${lineElementClasses.root}:not(.MuiLineElement-series-optival-line)`]: {
              strokeWidth: 2,
              opacity: 0.2,
              "&:hover": {
                strokeWidth: 3,
                opacity: 1,
              },
            },
            [`& .MuiChartsAxis-tickLabel`]: {
              fill: "grey !important",
            },
            [`& .MuiChartsLegend-root text`]: {
              fill: "grey !important",
            },
            [`& .${markElementClasses.root}`]: {
              opacity: showMarks ? 1 : 0,
              scale: showMarks ? "1" : "0.6",
              strokeWidth: 2,
              transition: "opacity 0.2s ease, scale 0.2s ease",
              "&:hover": {
                scale: "1.2",
                strokeWidth: 5,
                opacity: 0.8,
              },
            },

            // ✅ Hide the right Y-axis line
            "& .MuiChartsAxis-right .MuiChartsAxis-line": {
              stroke: "none !important",
            },

            // ✅ Hide the right Y-axis ticks
            "& .MuiChartsAxis-tick": {
              stroke: "none !important",
            },

            // ✅ Optionally hide the right Y-axis labels too
            "& .MuiChartsAxis-line": {
              display: "none",
            },
          }}
          slotProps={{
            legend: {
              direction: "row",
              position: {
                vertical: "bottom",
                horizontal: "left",
              },
              labelStyle: {
                fontSize: 12,
              },
              itemMarkWidth: 12,
              itemMarkHeight: 2,
              markGap: 8,
              itemGap: 10,
              padding: 28,
            },
          }}
          height={400}
        >
          <ChartsGrid horizontal />
          <CustomItemTooltip dataset={chartData} colorMap={colorMap} />
          <LinePlot />
          <MarkPlot />
        </LineChartPro>
      </Box>
    </Paper>
  );
};

export default DailyaverageImpressionShare;
